import { V3dSceneManager } from './v3d-scene-manager';
import { Box3, Object3D, OrthographicCamera, Sphere, Vector2, Vector3 } from 'three';
import { globalV3dSettings } from '../data/visualize-3d-global-settings';
import { preserveFrustumAspectRatio } from '../utility/viewer/orthographic-camera-utility';
import { ViewPoint } from '../models/view-point';

const viewMargin = 0.1;

export class V3dOrthographicSceneManager extends V3dSceneManager<OrthographicCamera> {
  public updateCameraAspectRatio(): void {
    // Orthographic camera's aspect ratio is preserved via it's frustum
    let frustumSize = new Vector2(this.camera.right - this.camera.left, this.camera.top - this.camera.bottom);
    frustumSize = preserveFrustumAspectRatio(frustumSize, this.canvasWidth / this.canvasHeight);
    this.camera.top = frustumSize.y / 2;
    this.camera.bottom = -frustumSize.y / 2;
    this.camera.left = -frustumSize.x / 2;
    this.camera.right = frustumSize.x / 2;
    this.camera.updateProjectionMatrix();
  }

  public lookAtObjectFromViewPoint(viewPoint: ViewPoint, target: Object3D): void {
    const bb = new Box3().setFromObject(target);

    const useSphericalSizeCalculation = this.viewPointUsesSphericalCameraFit(viewPoint);

    const frustDimensions = this.getSizeWithMargin(bb, useSphericalSizeCalculation);
    const aspectRatio = this.canvasWidth / this.canvasHeight;

    const lookAtPosition = bb.getCenter(new Vector3());

    const cameraPositionViewPoint = viewPoint;
    const cameraPosition = this.getCameraPosition(cameraPositionViewPoint, lookAtPosition);
    const cameraFrustumSize = this.getCameraFrustumSize(cameraPositionViewPoint, frustDimensions, aspectRatio);

    this.camera.position.set(cameraPosition.x, cameraPosition.y, cameraPosition.z);
    this.camera.zoom = 1;

    this.updateCameraInstantly(this.camera, cameraFrustumSize, lookAtPosition);
    this.render();
  }

  fitTargetInView(viewPoint: ViewPoint, target: Object3D): void {
    this.lookAtObjectFromViewPoint(viewPoint, target);
  }

  /**
   * Checks if a sphere should be used for calculating the bounding box of the model. This is needed for viewpoints which
   * look at the object from a non-straight angle.
   * @param viewPoint
   */
  private viewPointUsesSphericalCameraFit(viewPoint: ViewPoint): boolean {
    return viewPoint === ViewPoint.TopLeft || viewPoint === ViewPoint.TopRight;
  }

  private getSizeWithMargin(bb: Box3, useSphericalBounds = false): Vector3 {
    const boundsSize = bb.getSize(new Vector3());

    if (useSphericalBounds) {
      const boundingSphere = bb.getBoundingSphere(new Sphere());
      boundingSphere.getBoundingBox(new Box3()).getSize(boundsSize);
    }

    // Minimum margin which is left to one side of the object. Depending on the aspect ratio, the margin might be bigger on the other side.
    const finalSize = new Vector3(boundsSize.x, boundsSize.y, boundsSize.z);
    finalSize.x += viewMargin;
    finalSize.y += viewMargin;
    finalSize.z += viewMargin;

    return finalSize;
  }

  private getCameraPosition(viewPoint: ViewPoint, origin: Vector3): Vector3 {
    const distance = 5000;

    switch (viewPoint) {
      case ViewPoint.Front:
        return new Vector3(origin.x, origin.y - distance, origin.z);
      case ViewPoint.Back:
        return new Vector3(origin.x, origin.y + distance, origin.z);
      case ViewPoint.Left:
        return new Vector3(origin.x - distance, origin.y, origin.z);
      case ViewPoint.Right:
        return new Vector3(origin.x + distance, origin.y, origin.z);
      case ViewPoint.Top:
        return new Vector3(origin.x, origin.y, origin.z + distance);
      case ViewPoint.Bottom:
        return new Vector3(origin.x, origin.y, origin.z - distance);
      case ViewPoint.TopRight:
        return new Vector3(origin.x + distance, origin.y - distance, origin.z + distance);
      case ViewPoint.TopLeft:
        return new Vector3(origin.x - distance, origin.y - distance, origin.z + distance);
    }
  }

  private getCameraFrustumSize(viewPoint: ViewPoint, dimensions: Vector3, aspectRatio: number): Vector2 {
    let frustumSize = this.getFrustumSize(viewPoint, dimensions);

    frustumSize = preserveFrustumAspectRatio(frustumSize, aspectRatio);
    return frustumSize;
  }

  private getFrustumSize(viewPoint: ViewPoint, dimensions: Vector3): Vector2 {
    switch (viewPoint) {
      case ViewPoint.Front:
      case ViewPoint.Back:
        return new Vector2(dimensions.x, dimensions.z);
      case ViewPoint.Left:
      case ViewPoint.Right:
        return new Vector2(dimensions.y, dimensions.z);
      case ViewPoint.Top:
      case ViewPoint.Bottom:
        return new Vector2(dimensions.x, dimensions.y);
      case ViewPoint.TopRight:
      case ViewPoint.TopLeft:
        return new Vector2(dimensions.x, Math.max(dimensions.y, dimensions.z));
      default:
        throw new Error(`View point ${viewPoint} is not supported`);
    }
  }

  private updateCameraInstantly(camera: OrthographicCamera, newFrustumSize: Vector2, lookAt: Vector3): void {
    camera.top = newFrustumSize.y / 2;
    camera.bottom = -newFrustumSize.y / 2;
    camera.left = -newFrustumSize.x / 2;
    camera.right = newFrustumSize.x / 2;

    if (this.controls) {
      if (lookAt) {
        this.controls.target.set(lookAt.x, lookAt.y, lookAt.z);
      }
      this.controls.update();
    }

    camera.updateProjectionMatrix();
  }
}

export const createDefaultOrthographicCamera = (canvasWidth: number, canvasHeight: number) => {
  const camera = new OrthographicCamera(
    canvasWidth / -2,
    canvasWidth / 2,
    canvasHeight / 2,
    canvasHeight / -2,
    globalV3dSettings.cameraNearPlane,
    globalV3dSettings.cameraFarPlane,
  );
  camera.up.copy(globalV3dSettings.upVector);
  camera.position.set(0, -globalV3dSettings.cameraFarPlane / 2, 0);
  camera.updateProjectionMatrix();

  return camera;
};
